import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiBarChart2, FiDollarSign, FiEye, FiMousePointer, FiTarget, FiTrendingUp } from "react-icons/fi";
import Apis from "../../../../api";
import PCard from "../../../common/Card";
import PTable from "../../../common/Table";

interface DashboardCampaign {
    campaign_id: string;
    name: string;
    campaign_type: string;
    budget_type: string;
    budget_currency: string | null;
    budget: string;
    budget_spent: number;
    revenue: number;
    impressions: number;
    clicks: number;
    status: string;
    start_dt: string;
    end_dt: string | null;
}

interface UserDashboardData {
    user_name: string;
    user_token: string;
    campaigns: DashboardCampaign[];
}

interface UserDashboardResponse {
    status: string;
    statusMessage?: string;
    data?: UserDashboardData;
}

const formatCurrency = (value: number | string | null | undefined, currency?: string | null) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) {
        return "--";
    }

    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency || "USD",
        maximumFractionDigits: 2,
    }).format(amount);
};

const formatCount = (value: number) => new Intl.NumberFormat("en-US").format(value || 0);

const UserDashboard: React.FC = () => {
    const { user_token: userToken } = useParams<{ user_token: string }>();
    const navigate = useNavigate();
    const [dashboard, setDashboard] = useState<UserDashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!userToken) {
            setError("A user token is required to load this dashboard.");
            setLoading(false);
            return;
        }

        let isCurrent = true;
        setLoading(true);
        setError(null);

        Apis.fetchUserDashboard(userToken)
            .then(({ data }: { data: UserDashboardResponse }) => {
                if (!isCurrent) {
                    return;
                }
                if (data?.status !== "success" || !data.data) {
                    setError(data?.statusMessage || "Unable to load this user dashboard.");
                    return;
                }
                setDashboard(data.data);
            })
            .catch((requestError: unknown) => {
                if (!isCurrent) {
                    return;
                }
                const message = typeof requestError === "string"
                    ? requestError
                    : requestError instanceof Error
                        ? requestError.message
                        : "Unable to load this user dashboard.";
                setError(message);
            })
            .finally(() => {
                if (isCurrent) {
                    setLoading(false);
                }
            });

        return () => {
            isCurrent = false;
        };
    }, [userToken]);

    const campaigns = dashboard?.campaigns || [];
    const totals = campaigns.reduce((summary, campaign) => ({
        budget: summary.budget + (Number(campaign.budget) || 0),
        spent: summary.spent + (Number(campaign.budget_spent) || 0),
        revenue: summary.revenue + (Number(campaign.revenue) || 0),
        impressions: summary.impressions + (Number(campaign.impressions) || 0),
        clicks: summary.clicks + (Number(campaign.clicks) || 0),
    }), { budget: 0, spent: 0, revenue: 0, impressions: 0, clicks: 0 });

    if (loading) {
        return <p className="py-8 text-center text-gray-600">Loading user dashboard...</p>;
    }

    if (error) {
        return (
            <section className="space-y-5">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="rounded-lg bg-[#C2D9CD] px-3 py-2 text-sm font-medium text-[#056433] hover:bg-[#b4d0c1]"
                    >
                        Back
                    </button>
                    <h1 className="text-2xl font-medium text-gray-900">User Dashboard</h1>
                </div>
                <PCard className="border border-red-200 p-6">
                    <p role="alert" className="text-red-700">{error}</p>
                </PCard>
            </section>
        );
    }

    if (!dashboard) {
        return null;
    }

    const summaryCards = [
        { label: "Campaigns created", value: formatCount(campaigns.length), icon: <FiTarget /> },
        { label: "Total budget", value: formatCurrency(totals.budget), icon: <FiDollarSign /> },
        { label: "Budget spent", value: formatCurrency(totals.spent), icon: <FiTrendingUp /> },
        { label: "Revenue", value: formatCurrency(totals.revenue), icon: <FiBarChart2 /> },
        { label: "Impressions", value: formatCount(totals.impressions), icon: <FiEye /> },
        { label: "Clicks", value: formatCount(totals.clicks), icon: <FiMousePointer /> },
    ];

    const campaignColumns = [
        {
            title: "Campaign",
            dataIndex: "name",
            width: 220,
            render: (name: string) => <span className="font-medium">{name || "--"}</span>,
        },
        {
            title: "Type",
            dataIndex: "campaign_type",
            width: 100,
            render: (type: string) => type || "--",
        },
        {
            title: "Budget",
            dataIndex: "budget",
            width: 160,
            sorter: (a: DashboardCampaign, b: DashboardCampaign) => Number(a.budget) - Number(b.budget),
            render: (_: string, campaign: DashboardCampaign) => (
                <>
                    {formatCurrency(campaign.budget, campaign.budget_currency)}
                    {campaign.budget_type ? ` / ${campaign.budget_type}` : ""}
                </>
            ),
        },
        {
            title: "Spent",
            dataIndex: "budget_spent",
            width: 120,
            sorter: (a: DashboardCampaign, b: DashboardCampaign) => a.budget_spent - b.budget_spent,
            render: (spent: number, campaign: DashboardCampaign) => formatCurrency(spent, campaign.budget_currency),
        },
        {
            title: "Revenue",
            dataIndex: "revenue",
            width: 120,
            sorter: (a: DashboardCampaign, b: DashboardCampaign) => a.revenue - b.revenue,
            render: (revenue: number, campaign: DashboardCampaign) => formatCurrency(revenue, campaign.budget_currency),
        },
        {
            title: "Impressions",
            dataIndex: "impressions",
            width: 120,
            sorter: (a: DashboardCampaign, b: DashboardCampaign) => a.impressions - b.impressions,
            render: (impressions: number) => formatCount(impressions),
        },
        {
            title: "Clicks",
            dataIndex: "clicks",
            width: 90,
            sorter: (a: DashboardCampaign, b: DashboardCampaign) => a.clicks - b.clicks,
            render: (clicks: number) => formatCount(clicks),
        },
        {
            title: "Status",
            dataIndex: "status",
            width: 120,
            render: (status: string) => status || "--",
        },
        {
            title: "Start date",
            dataIndex: "start_dt",
            width: 160,
            render: (startDate: string) => startDate || "--",
        },
        {
            title: "End date",
            dataIndex: "end_dt",
            width: 160,
            render: (endDate: string | null) => endDate || "--",
        },
    ];

    return (
        <main className="space-y-5">
            <header className="flex min-h-[70px] flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="rounded-lg bg-[#C2D9CD] px-3 py-2 text-sm font-medium text-[#056433] hover:bg-[#b4d0c1]"
                    >
                        Back
                    </button>
                    <div>
                        <h1 className="text-2xl font-medium text-gray-900">{dashboard.user_name || "User Dashboard"}</h1>
                        <p className="mt-1 text-sm text-gray-500">
                            User dashboard <span className="mx-1 text-gray-300">/</span>
                            {formatCount(campaigns.length)} {campaigns.length === 1 ? "campaign" : "campaigns"} created
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-[#F6F9F7] px-4 py-2 text-sm font-medium text-[#056433]">
                    <FiTarget aria-hidden="true" />
                    <span>{formatCount(campaigns.length)} total campaigns</span>
                </div>
            </header>

            <section aria-label="Campaign summary" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {summaryCards.map((card) => (
                    <PCard
                        key={card.label}
                        className="topCard flex min-h-[126px] w-full cursor-default flex-col justify-between px-4 py-3 font-[Roboto]"
                    >
                        <div className="flex items-center justify-between gap-3">
                            <p className="title mb-0 text-xs font-medium text-[#056433]">{card.label}</p>
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F6F9F7] text-lg text-[#056433]">
                                {card.icon}
                            </span>
                        </div>
                        <p className="value mb-1 mt-4 break-words text-2xl font-semibold text-gray-900">{card.value}</p>
                    </PCard>
                ))}
            </section>

            <PCard>
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-5 py-4">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Campaign performance</h2>
                        <p className="mt-1 text-sm text-gray-500">Campaigns created by {dashboard.user_name || "this user"}</p>
                    </div>
                    <span className="rounded-full bg-[#F6F9F7] px-3 py-1 text-xs font-medium text-[#056433]">
                        {formatCount(campaigns.length)} {campaigns.length === 1 ? "campaign" : "campaigns"}
                    </span>
                </div>
                <PTable
                    loading={loading}
                    pagination={{ isShow: true }}
                    columns={campaignColumns}
                    data={campaigns}
                    className="mt-5"
                    rowKey={(campaign: DashboardCampaign) => campaign.campaign_id}
                />
            </PCard>
        </main>
    );
};

export default UserDashboard;
